import ClearIcon from '@mui/icons-material/Clear';
import {InputAdornment, type SxProps} from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, {type SelectChangeEvent} from '@mui/material/Select';
import {lighten} from '@mui/system/colorManipulator';
import type {FC} from 'react';
import {secondaryColor} from '@/theme/theme';

export type Option = {
  id: string;
  label: string;
};

export type MultiSelectFilterProps = {
  placeholder: string;
  options: Array<Option>;
  selectedOptions: Array<Option>;
  onOptionsChanged: (options: Array<Option>) => void;
  sx?: SxProps;
};

const MultiSelect: FC<MultiSelectFilterProps> = ({placeholder, options, selectedOptions, onOptionsChanged}) => {
  const handleChange = (e: SelectChangeEvent<Array<string>>) => {
    const {value} = e.target;
    const ids = typeof value === 'string' ? value.split(',') : value;
    const newSelectedOptions = options.filter((o) => ids.includes(o.label));
    onOptionsChanged(newSelectedOptions);
  };

  const handleDelete = (id: string) => {
    onOptionsChanged(selectedOptions.filter((o) => o.id !== id));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOptionsChanged([]);
  };

  return (
    <>
      <Select
        multiple
        size="small"
        displayEmpty
        value={selectedOptions.map((o) => o.label)}
        onChange={handleChange}
        input={
          <OutlinedInput
            notched={false}
            endAdornment={
              selectedOptions.length > 0 && (
                <InputAdornment position="end" sx={{pr: 2}}>
                  <IconButton size="small" onClick={handleClear} className="clear-button" tabIndex={-1}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        }
        renderValue={(selected) => (selected.length === 0 ? placeholder : placeholder)}
        sx={styles.select}
      >
        {options.map(({id, label}) => (
          <MenuItem key={id} value={label}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <Box sx={styles.chips}>
        {selectedOptions &&
          selectedOptions.length > 0 &&
          selectedOptions.map(({id, label}) => (
            <Chip key={id} label={label} size="small" onDelete={() => handleDelete(id)} sx={styles.chip} />
          ))}
      </Box>
    </>
  );
};

export default MultiSelect;

const styles: Record<string, SxProps> = {
  select: {
    bgcolor: lighten(secondaryColor, 0.95),
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderRadius: '0px',
        borderColor: 'primary.main', // borde hover
      },
      '&.Mui-focused fieldset': {
        borderColor: 'secondary.main', // borde focus
      },
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 1,
  },
  chip: {
    bgcolor: 'primary.light',
    color: 'common.white',
    fontWeight: 700,
    '& .MuiChip-deleteIcon': {
      color: 'common.white',
    },
  },
  chips: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    maxWidth: '80vw',
    flexWrap: 'wrap',
    mt: 1,
    mb: 2,
  },
};
